/**
 * The alignment canvas — "mark the part that lines up" (docs/design-alignment-plan-v1.md § 4.2).
 *
 * The art on a 1-inch grid at true print scale, with a draggable box over it. The grid is drawn in
 * PERCENTAGES of the art box, so it stays exactly one inch per cell at any rendered size without a
 * single measurement in JS. All geometry lives in `design-align-lib`; this file only turns pointers
 * and keys into calls on it.
 *
 * The box snaps to ⅛ in, which is as fine as a judgment about a heat press is worth.
 */

import { useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  ALIGN_SNAP_IN,
  type AlignBox,
  type BoxCorner,
  boxInInches,
  moveBox,
  resizeBox,
} from './design-align-lib';

const CORNERS: readonly BoxCorner[] = ['nw', 'ne', 'sw', 'se'];
const CORNER_CLASS: Record<BoxCorner, string> = {
  nw: '-left-2 -top-2 cursor-nwse-resize',
  ne: '-right-2 -top-2 cursor-nesw-resize',
  sw: '-left-2 -bottom-2 cursor-nesw-resize',
  se: '-right-2 -bottom-2 cursor-nwse-resize',
};

export interface AlignCanvasProps {
  /** Object URL for the file being aligned. */
  src: string;
  /** True print size, from the file's pixels at 300 DPI. Drives the grid and the snap step. */
  fileWIn: number;
  fileHIn: number;
  box: AlignBox;
  onChange: (next: AlignBox) => void;
  disabled?: boolean;
  label?: string;
}

export function AlignCanvas({
  src,
  fileWIn,
  fileHIn,
  box,
  onChange,
  disabled = false,
  label = 'Alignment box',
}: AlignCanvasProps) {
  const artRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    corner: BoxCorner | null;
    startX: number;
    startY: number;
    start: AlignBox;
    w: number;
    h: number;
  } | null>(null);

  function begin(e: React.PointerEvent, corner: BoxCorner | null) {
    if (disabled || e.button !== 0) return;
    const rect = artRef.current?.getBoundingClientRect();
    // No rect means nothing has been laid out yet (jsdom, or a hidden tab) — a drag would divide by
    // zero and fling the box, so refuse to start one.
    if (!rect || rect.width <= 0 || rect.height <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      corner,
      startX: e.clientX,
      startY: e.clientY,
      start: box,
      w: rect.width,
      h: rect.height,
    };
  }

  function move(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.w;
    const dy = (e.clientY - d.startY) / d.h;
    onChange(
      d.corner
        ? resizeBox(d.start, d.corner, dx, dy, fileWIn, fileHIn)
        : moveBox(d.start, dx, dy, fileWIn, fileHIn),
    );
  }

  // A pointer interaction can finish in more ways than pointerup: a cancelled gesture and a lost
  // capture both end the drag, and leaving either one out strands the handler mid-drag.
  const end = () => {
    drag.current = null;
  };

  function nudge(e: React.KeyboardEvent) {
    if (disabled) return;
    const stepX = ALIGN_SNAP_IN / fileWIn;
    const stepY = ALIGN_SNAP_IN / fileHIn;
    const by: Record<string, [number, number]> = {
      ArrowLeft: [-stepX, 0],
      ArrowRight: [stepX, 0],
      ArrowUp: [0, -stepY],
      ArrowDown: [0, stepY],
    };
    const delta = by[e.key];
    if (!delta) return;
    e.preventDefault();
    onChange(moveBox(box, delta[0], delta[1], fileWIn, fileHIn));
  }

  const pct = (v: number) => `${v * 100}%`;
  const inches = boxInInches(box, fileWIn, fileHIn);

  return (
    <div
      className="grid h-full min-h-0 place-items-center overflow-hidden rounded-md bg-black p-3"
      data-testid="align-stage"
      // A SIZE container, so the art box below can size itself against this box's height in `cqh`.
      // ⚠️ Size containment means this element's own size cannot come from its content — `h-full`
      // above is what gives it a definite one. Without that it collapses to zero.
      style={{ containerType: 'size' }}
    >
      {/* The art box IS the file's rectangle, so every percentage below is a true fraction of the
          file — no letterboxing to compensate for and no measurement to keep in sync. */}
      <div
        ref={artRef}
        data-testid="align-art"
        className="relative max-w-full overflow-hidden"
        // Fit to BOTH axes — the same trick `platenFrameStyle` uses for the platen. `aspect-ratio`
        // alone cannot do this: with a definite `height: 100%` it computes the WIDTH, `max-width`
        // then clamps that width, and the height never shrinks back — so a WIDE design comes out
        // stretched. Deriving the width from the container's height instead makes the ratio hold on
        // both axes, for portrait and landscape art alike.
        style={{
          width: `min(100%, calc(100cqh * ${fileWIn} / ${fileHIn}))`,
          aspectRatio: `${fileWIn} / ${fileHIn}`,
        }}
      >
        <img src={src} alt="" className="h-full w-full object-fill" draggable={false} />
        {/* One cell per print inch, in percentages of the art box. */}
        <div
          aria-hidden
          data-testid="align-grid"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.14) 1px, transparent 1px),' +
              'linear-gradient(to bottom, rgba(255,255,255,0.14) 1px, transparent 1px)',
            backgroundSize: `${100 / fileWIn}% ${100 / fileHIn}%`,
          }}
        />
        <div
          data-testid="align-box"
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-valuetext={`${inches.wIn.toFixed(2)} by ${inches.hIn.toFixed(2)} inches, centre ${inches.centreIn.toFixed(2)} inches from the left edge`}
          aria-valuemin={0}
          aria-valuemax={fileWIn}
          aria-valuenow={Number(inches.centreIn.toFixed(2))}
          className={cn(
            'absolute touch-none select-none border-2 border-emerald-400 outline-none',
            'focus-visible:ring-2 focus-visible:ring-emerald-300',
            disabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
          )}
          style={{
            left: pct(box.x),
            top: pct(box.y),
            width: pct(box.w),
            height: pct(box.h),
            // Dims everything outside the box, clipped by the art container above.
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          }}
          onPointerDown={(e) => begin(e, null)}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onLostPointerCapture={end}
          onKeyDown={nudge}
        >
          <span
            aria-hidden
            className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-emerald-400/70"
          />
          {!disabled &&
            CORNERS.map((corner) => (
              <span
                key={corner}
                data-testid={`align-handle-${corner}`}
                className={cn(
                  'absolute size-4 touch-none rounded-sm border-2 border-emerald-950 bg-emerald-400',
                  CORNER_CLASS[corner],
                )}
                onPointerDown={(e) => begin(e, corner)}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
                onLostPointerCapture={end}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
